import { cva } from 'class-variance-authority';

/**
 * Variant recipes for the shared UI primitives. Kept together so the same
 * intents (ink, gold, ghost…) mean the same thing on every component.
 */

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-deen-gold focus-visible:ring-offset-2 focus-visible:ring-offset-white';

export const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap select-none transition-all duration-200 disabled:opacity-40 disabled:pointer-events-none ${focusRing}`,
  {
    variants: {
      intent: {
        primary: 'bg-deen-ink text-white shadow-sm hover:bg-deen-ink/90 hover:shadow-md',
        gold: 'bg-deen-gold text-deen-ink shadow-sm hover:bg-deen-gold/80',
        secondary: 'bg-white text-deen-ink border border-deen-ink/10 shadow-sm hover:border-deen-ink/25 hover:shadow-md',
        ghost: 'text-deen-ink/70 hover:text-deen-ink hover:bg-deen-ink/5',
        inverse: 'bg-white/10 text-white border border-white/15 hover:bg-white/20',
      },
      size: {
        sm: 'h-8 px-3 text-xs rounded-xl',
        md: 'h-10 px-4 text-sm rounded-2xl',
        lg: 'h-12 px-6 text-sm rounded-2xl',
      },
      iconOnly: { true: 'px-0 aspect-square', false: '' },
      block: { true: 'w-full', false: '' },
      active: { true: '', false: '' },
    },
    compoundVariants: [
      { intent: 'ghost', active: true, class: '!bg-deen-ink/5 !text-deen-ink' },
      { intent: 'secondary', active: true, class: '!border-deen-ink !bg-deen-ink !text-white' },
    ],
    defaultVariants: { intent: 'primary', size: 'md', iconOnly: false, block: false, active: false },
  }
);

export const badgeVariants = cva('inline-flex items-center gap-1.5 font-semibold whitespace-nowrap', {
  variants: {
    tone: {
      ink: 'bg-deen-ink/5 text-deen-ink border border-deen-ink/10',
      gold: 'bg-deen-gold/30 text-deen-ink border border-deen-gold/60',
      solid: 'bg-deen-ink text-white',
      muted: 'bg-slate-100 text-slate-600',
      inverse: 'bg-white/10 text-white border border-white/15',
    },
    size: {
      sm: 'text-[10px] px-2 py-0.5 rounded-lg uppercase tracking-widest',
      md: 'text-xs px-3 py-1 rounded-full',
    },
  },
  defaultVariants: { tone: 'ink', size: 'md' },
});

export const cardVariants = cva('relative', {
  variants: {
    tone: {
      plain: 'bg-white border border-deen-ink/5 shadow-sm',
      mist: 'bg-deen-mist border border-deen-ink/5',
      sand: 'bg-deen-sand/60 border border-deen-gold/30',
      ink: 'bg-deen-ink text-white',
      glass: 'bg-white/70 backdrop-blur-xl border border-white shadow-[0_20px_60px_-20px_rgba(28,72,72,0.25)]',
    },
    padding: { none: '', sm: 'p-4', md: 'p-6', lg: 'p-8' },
    radius: { md: 'rounded-2xl', lg: 'rounded-3xl', xl: 'rounded-[32px]' },
    interactive: {
      true: `transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:border-deen-ink/10 ${focusRing}`,
      false: '',
    },
  },
  defaultVariants: { tone: 'plain', padding: 'md', radius: 'lg', interactive: false },
});

export const segmentedVariants = cva('inline-flex items-center gap-1 p-1 rounded-2xl', {
  variants: {
    tone: {
      light: 'bg-deen-ink/5',
      dark: 'bg-white/10',
    },
    block: { true: 'w-full', false: '' },
  },
  defaultVariants: { tone: 'light', block: false },
});

export const segmentVariants = cva(
  `flex-1 inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-semibold rounded-xl transition-all duration-200 ${focusRing}`,
  {
    variants: {
      size: { sm: 'h-7 px-2.5 text-xs', md: 'h-9 px-3.5 text-sm' },
      selected: { true: '', false: '' },
      tone: { light: '', dark: '' },
    },
    compoundVariants: [
      { tone: 'light', selected: true, class: 'bg-white text-deen-ink shadow-sm' },
      { tone: 'light', selected: false, class: 'text-deen-ink/50 hover:text-deen-ink' },
      { tone: 'dark', selected: true, class: 'bg-white text-deen-ink' },
      { tone: 'dark', selected: false, class: 'text-white/60 hover:text-white' },
    ],
    defaultVariants: { size: 'md', selected: false, tone: 'light' },
  }
);

export const fieldVariants = cva(
  'group relative flex items-center w-full bg-white border transition-all duration-200 focus-within:border-deen-ink/30 focus-within:shadow-[0_0_0_4px_rgba(236,215,159,0.35)]',
  {
    variants: {
      size: {
        md: 'h-11 rounded-2xl pl-11 pr-2 text-sm',
        lg: 'h-14 rounded-2xl pl-12 pr-2 text-base',
        xl: 'h-16 rounded-[22px] pl-14 pr-2.5 text-base sm:text-lg',
      },
      tone: {
        plain: 'border-deen-ink/10 shadow-sm',
        elevated: 'border-white shadow-[0_24px_60px_-24px_rgba(28,72,72,0.35)]',
      },
    },
    defaultVariants: { size: 'md', tone: 'plain' },
  }
);
